import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// FSM Dashboard API Service
export const fsmDashboardApi = createApi({
  reducerPath: 'fsmDashboardApi',
	baseQuery: fetchBaseQuery({
		baseUrl: `${process.env.REACT_APP_BACKEND_URL}/fsmDashboard`,
		credentials: "include",
	}),
  tagTypes: [
    'Overview',
    'Tasks', 
    'Technicians',
    'ServiceRequests',
    'Geofence',
    'Forms',
    'Alerts',
    'Resources',
    'employee',
    'Performance',
    'Monthly',
    'SlaViolation'
  ],
  endpoints: (builder) => ({
    // Main overview statistics (KPI cards)
    getOverviewStatistics: builder.query({
      query: () => '/overview',
      providesTags: ['Overview'],
      transformResponse: (response) => {
        // Transform backend response to match frontend expectations
        return {
          activeTasks: { value: response?.activeTasks || 0, trend: response?.activeTasksTrend || 0, status: response?.activeTasksTrend >= 0 ? 'up' : 'down' },
          activeTechnicians: { value: response?.activeTechnicians || 0, trend: response?.activeTechniciansTrend || 0, status: response?.activeTechniciansTrend >= 0 ? 'up' : 'down' },
          completionRate: { value: response?.completionRate || 0, trend: response?.completionRateTrend || 0, status: response?.completionRateTrend >= 0 ? 'up' : 'down' },
          avgResponseTime: { value: response?.avgResponseTime || 0, trend: response?.avgResponseTimeTrend || 0, status: response?.avgResponseTimeTrend <= 0 ? 'up' : 'down' },
          pendingRequests: { value: response?.pendingRequests || 0, trend: response?.pendingRequestsTrend || 0, status: response?.pendingRequestsTrend <= 0 ? 'up' : 'down' },
          slaCompliance: { value: response?.slaCompliance || 0, trend: response?.slaComplianceTrend || 0, status: response?.slaComplianceTrend >= 0 ? 'up' : 'down' },
          ...response
        };
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch overview statistics'
      })
    }),

    // Task-specific metrics with date range support
    getTaskMetrics: builder.query({
      query: (params = {}) => ({
        url: '/tasks/metrics',
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          ...params
        }
      }),
      providesTags: ['Tasks'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch task metrics'
      })
    }),

    // Technician utilization and status
    getTechnicianMetrics: builder.query({
      query: () => '/technicians/status',
      providesTags: ['Technicians'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch technician metrics'
      })
    }),

    // Service request pipeline
    getServiceRequestMetrics: builder.query({
      query: ({ from, to }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('from', from);
        queryParams.append('to', to);
        return `/service-requests/pipeline?${queryParams.toString()}`;
      },
      providesTags: ['ServiceRequests'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch service request metrics'
      })
    }),

    // Geofence and POI metrics
    getGeofenceMetrics: builder.query({
      query: () => '/geofence/metrics',
      providesTags: ['Geofence'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch geofence metrics'
      })
    }),

    // Form submission metrics
    getFormMetrics: builder.query({
      query: ({ from, to }) => {
          const queryParams = new URLSearchParams();
          queryParams.append('from', from);
          queryParams.append('to', to);
          return `/forms/metrics?${queryParams.toString()}`;
        },
      providesTags: ['Forms'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch form metrics'
      })
    }),

    // Monthly comparison data
    getMonthlyComparison: builder.query({
      query: () => '/monthly-comparison',
      providesTags: ['Monthly'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch monthly comparison'
      })
    }),

    // Performance trends
    getPerformanceTrends: builder.query({
      query: () => '/performance-trends',
      providesTags: ['Performance'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch performance trends'
      })
    }),

    // Alerts summary (for alert cards)
    getAlertsSummary: builder.query({
      query: () => '/alerts',
      providesTags: ['Alerts'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch alerts summary'
      })
    }),

    // Resource status panel
    getResourceStatus: builder.query({
      query: () => '/resource-status',
      providesTags: ['Resources'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch resource status'
      })
    }),
        getEmployeePerformance: builder.query({
      query: () => '/employee-performance',
      providesTags: ['Resources'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch employee dashboard'
      })
    }),

    // Task distribution by type/priority
    getTaskDistribution: builder.query({
        query: ({ from, to }) => {
          const queryParams = new URLSearchParams();
          queryParams.append('from', from);
          queryParams.append('to', to);
          return `/task-distribution?${queryParams.toString()}`;
        },
      providesTags: ['Tasks'],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || 'Failed to fetch task distribution'
      })
    }),

    getSlaViolation: builder.query({
      query: ({ page, limit , from, to}) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page);
        queryParams.append('pageSize', limit);
        queryParams.append('dateFrom', from);
        queryParams.append('dateTo', to);
        return `/popups/sla-violation?${queryParams.toString()}`;
      },
    })
  }),
});

// Export hooks for usage in functional components
export const {
  useGetOverviewStatisticsQuery,
  useGetTaskMetricsQuery,
  useGetTechnicianMetricsQuery,
  useGetServiceRequestMetricsQuery,
  useGetGeofenceMetricsQuery,
  useGetFormMetricsQuery,
  useGetMonthlyComparisonQuery,
  useGetPerformanceTrendsQuery,
  useGetAlertsSummaryQuery,
  useGetResourceStatusQuery,
  useGetEmployeePerformanceQuery,
  useGetTaskDistributionQuery,
  useLazyGetTaskMetricsQuery,
  useLazyGetOverviewStatisticsQuery,
  useGetSlaViolationQuery
} = fsmDashboardApi;

// Export the reducer and middleware
export default fsmDashboardApi;

